import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiClient } from '../lib/api';
import type { UserWithOrganization, Organization } from '../lib/auth-types';

// Demo-mode flag and hardcoded user for offline/DB-broken scenarios.
// Falls back to enabling demo in production if the env var is missing,
// so Vercel previews work without manual config. Set REACT_APP_DEMO_AUTH=false
// to force real auth when backend is ready.
const demoEnvFlag = process.env.REACT_APP_DEMO_AUTH;
const DEMO_MODE = demoEnvFlag === 'true' || (demoEnvFlag === undefined && process.env.NODE_ENV === 'production');
const DEMO_CREDENTIALS = {
  email: 'admin@stmarys.com',
  password: 'admin123',
};
const DEMO_USER: UserWithOrganization = {
  id: '00000000-0000-0000-0000-000000000001',
  email: DEMO_CREDENTIALS.email,
  organization_id: '00000000-0000-0000-0000-000000000001',
  role: 'admin',
  department: 'Administration',
  preferences: {},
  is_active: true,
  email_verified: true,
  last_login_at: new Date().toISOString(),
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
  organization: {
    id: '00000000-0000-0000-0000-000000000001',
    name: "St. Mary's Hospital (Demo)",
    settings: {},
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
};

interface AuthContextType {
  user: UserWithOrganization | null;
  organization: Organization | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<UserWithOrganization | null>(null);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check for existing session on mount
  useEffect(() => {
    const initAuth = async () => {
      if (DEMO_MODE) {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          try {
            const parsed = JSON.parse(storedUser);
            setUser(parsed);
            setOrganization(parsed.organization);
          } catch {
            localStorage.removeItem('user');
          }
        }
        setIsLoading(false);
        return;
      }

      const token = localStorage.getItem('auth_token');
      if (token) {
        try {
          // Validate token by fetching current user
          const currentUser = await apiClient.getCurrentUser();
          setUser(currentUser);
          setOrganization(currentUser.organization);
        } catch (error) {
          console.error('Session validation failed:', error);
          // Clear invalid session
          localStorage.removeItem('auth_token');
          localStorage.removeItem('user');
        }
      }
      setIsLoading(false);
    };

    initAuth();
  }, []);

  // Listen for auth:logout events from axios interceptor
  useEffect(() => {
    const handleLogout = () => {
      setUser(null);
      setOrganization(null);
    };

    window.addEventListener('auth:logout', handleLogout);
    return () => window.removeEventListener('auth:logout', handleLogout);
  }, []);

  const login = async (email: string, password: string) => {
    try {
      if (DEMO_MODE) {
        if (email === DEMO_CREDENTIALS.email && password === DEMO_CREDENTIALS.password) {
          localStorage.setItem('auth_token', 'demo-token');
          localStorage.setItem('user', JSON.stringify(DEMO_USER));
          setUser(DEMO_USER);
          setOrganization(DEMO_USER.organization);
          return;
        }
        throw new Error('Invalid demo credentials');
      }

      const response = await apiClient.login({ email, password });

      // Store token
      localStorage.setItem('auth_token', response.token);

      // Store user data
      localStorage.setItem('user', JSON.stringify(response.user));
      
      // Update state
      setUser(response.user);
      setOrganization(response.user.organization);
    } catch (error: any) {
      console.error('Login failed:', error);
      throw new Error(error.response?.data?.detail || 'Login failed. Please check your credentials.');
    }
  };

  const logout = async () => {
    try {
      if (!DEMO_MODE) {
        // Call backend logout endpoint (optional)
        await apiClient.logout();
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear local storage
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      
      // Clear state
      setUser(null);
      setOrganization(null);
    }
  };

  const refreshUser = async () => {
    try {
      if (DEMO_MODE) {
        setUser(DEMO_USER);
        setOrganization(DEMO_USER.organization);
        localStorage.setItem('user', JSON.stringify(DEMO_USER));
        return;
      }

      const currentUser = await apiClient.getCurrentUser();
      setUser(currentUser);
      setOrganization(currentUser.organization);
      localStorage.setItem('user', JSON.stringify(currentUser));
    } catch (error) {
      console.error('Failed to refresh user:', error);
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    organization,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
